import { useRecipeTagStore } from "./recipe-tag-store";

import {
  bulkUpdateArchivedUserPath as recipesBulkUpdateArchivedPath,
  bulkRecipeCopiesUserPath,
} from "~/routes";

import { defineStore } from "pinia";
import { isEqual } from "lodash-es";

const storeInstances = new Map();

const validDate = (v) => {
  return typeof v === "string" && v.match(/^\d{4}-\d\d-\d\d$/);
};

const defaults = {
  userId: undefined,
  searchEndpoint: null,

  loaded: false,

  q: "",

  archived: false,
  subrecipe: null,
  empty: null,

  activeTags: [],

  beginDate: null,
  endDate: null,

  sortBy: "updated_at",
  sortDirection: "desc",

  subset: null,

  visibleRecipes: [],
  totalEntries: 0,
  currentPage: 1,
  perPage: 20,

  selection: [],
  _snapshotOfSelectionArchiveState: {},
  _knownArchivedRecipesExist: false,

  initialSettings: null,
};

/* State keys which have an effect on the returned results mapped to their URLSearchParam key */
const queryParamMap = {
  q: "q",
  archived: "archived",
  subrecipe: "subrecipe",
  empty: "empty",
  activeTags: "tags[]",
  perPage: "per_page",
  currentPage: "page",
  sortBy: "sort_by",
  sortDirection: "sort_direction",
  beginDate: "begin_date",
  endDate: "end_date",
  userId: "user_id",
};

// Keys that determine if advanced filters are shown or not
const advancedFilterKeys = ["subrecipe", "empty", "activeTags", "beginDate", "endDate"];

const validSorts = ["name", "updated_at"];

export const useRecipeSearchStore = (storeName = "recipeSearch") => {
  if (storeInstances.has(storeName)) {
    return storeInstances.get(storeName);
  }
  const useStore = defineStore(storeName, {
    persist: {
      storage: sessionStorage,
      paths: [
        "userId",
        "searchEndpoint",
        "q",
        "archived",
        "subrecipe",
        "empty",
        "activeTags",
        "sortBy",
        "sortDirection",
        "selection",
        "_snapshotOfSelectionArchiveState",
        "beginDate",
        "endDate",
      ],
    },

    state: () => {
      return {
        ...defaults,
        initialSettings: {},
      };
    },

    getters: {
      selectionWithNames: (state) => {
        return state.selection.map((id) => {
          const recipe = state.visibleRecipes.find((r) => r.id === id);
          return { id, name: recipe?.name ?? "Unknown" };
        });
      },

      selectionArchiveState: (state) => {
        return state.selection.map((id) => state._snapshotOfSelectionArchiveState[id] || "unknown");
      },

      knownArchivedRecipesExist: (state) => state._knownArchivedRecipesExist,

      queryParamMap: (state) => {
        return queryParamMap;
      },

      /* Returns the keys that the user has configured that affect filters */
      configuredFilterKeys: (state) => {
        const pristine = { ...defaults, ...(state.initialSettings ?? {}) };

        const diff = Object.keys(queryParamMap)
          .filter((k) => !isEqual(state[k], pristine[k]))
          .map((k) => [k, [state[k], pristine[k]]]);

        return Object.fromEntries(diff);
      },

      hasQuery: (state) => {
        return state.q !== "";
      },

      hasAdvancedFilters: (state) => {
        return (
          Object.keys(state.configuredFilterKeys).some((k) => advancedFilterKeys.includes(k)) ||
          state.archived === null
        );
      },

      requestQueryParams: (state) => {
        const p = new URLSearchParams();

        if (state.q !== "") {
          p.append(queryParamMap["q"], state.q);
        }

        if (state.archived !== null) {
          p.append(queryParamMap["archived"], state.archived);
        }

        if (state.subrecipe !== null) {
          p.append(queryParamMap["subrecipe"], state.subrecipe);
        }

        if (state.empty !== null) {
          p.append(queryParamMap["empty"], state.empty);
        }

        state.activeTags.forEach((t) => {
          p.append(queryParamMap["activeTags"], t.name);
        });

        p.append(queryParamMap["perPage"], state.perPage);
        p.append(queryParamMap["currentPage"], state.currentPage);
        p.append(queryParamMap["sortBy"], state.sortBy);
        p.append(queryParamMap["sortDirection"], state.sortDirection);

        if (validDate(state.beginDate)) {
          p.append(queryParamMap["beginDate"], state.beginDate);
        }

        if (validDate(state.endDate)) {
          p.append(queryParamMap["endDate"], state.endDate);
        }

        /// The normal recipes search endpoint is under /users/<userId>/, but the
        /// ingredients#show_recipes_for endpoint is /ingredients/<ingredientId>/ and in
        /// that case we need to add the `?user_id` URL query param.
        if (state.userId && !state.searchEndpoint.includes(`users/${state.userId}`)) {
          p.append(queryParamMap["userId"], state.userId);
        }

        return p;
      },

      requestQueryString() {
        return this.requestQueryParams.toString();
      },

      focusedOnSelection: (state) => state.subset !== null,
    },

    actions: {
      assignInitial(values = {}, { patch = false } = {}) {
        this.initialSettings = { ...values };

        if (patch) {
          this.$patch(values);
        }
      },

      setUserId(userId) {
        if (this.userId !== undefined && userId !== this.userId) {
          this.$reset();
        }

        this.userId = userId;
      },

      setSearchEndpoint(url) {
        this.searchEndpoint = url;
      },

      loadInitial({ force = false } = {}) {
        if (force) {
          this.loaded = false;
        }
        if (!this.loaded) {
          this.loadRecipePage();
        }
      },

      async loadRecipePage(newPage) {
        if (newPage !== undefined) {
          this.currentPage = newPage;
        }

        const response = await fetch(`${this.searchEndpoint}?${this.requestQueryString}`, {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
          },
          body: this.subset ? JSON.stringify({ subset: this.subset }) : null,
        });

        const json = await response.json();

        this.visibleRecipes = json.recipes;
        this.totalEntries = json.total_entries;
        this.perPage = json.per_page;
        this.currentPage = json.current_page;
        this.loaded = true;
      },

      setTagFilter(tags) {
        this.activeTags = tags;
        this.loadRecipePage(1);
      },

      setQueryFilter(q) {
        this.q = q;
        this.loadRecipePage(1);
      },

      setDateFilter({ begin, end }) {
        this.beginDate = begin;
        this.endDate = end;

        this.loadRecipePage(1);
      },

      async fetchSelection() {
        const response = await fetch(
          `${this.searchEndpoint}?format=ids&${this.requestQueryString}`,
          {
            method: "POST",
            headers: { Accept: "application/json" },
          }
        );

        return await response.json();
      },

      async bulkUpdateRecipesArchived(archived) {
        const data = { bulk_update_archived: { archived, recipe_ids: this.selection } };
        const response = await fetch(recipesBulkUpdateArchivedPath(this.userId), {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify(data),
        });

        const json = await response.json();

        if (response.ok) {
          this.selection.forEach((id) => {
            this._snapshotOfSelectionArchiveState[id] = archived ? "archived" : "unarchived";
          });
          if (archived) {
            this._knownArchivedRecipesExist = true;
          }
          return json.notice;
        } else {
          throw json?.error || "Failed to update recipes";
        }
      },

      async bulkCopyToAccount(targetUser) {
        const response = await fetch(bulkRecipeCopiesUserPath(this.userId), {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ target_user: targetUser, recipe_ids: this.selection }),
        });

        const json = await response.json();

        if (response.ok) return json.notice;
        throw json?.error || "Failed to start copy";
      },

      addToSelection(recipeIds) {
        try {
          this._updateSnapshotOfSelectionArchiveState(recipeIds);
        } catch (err) {
          console.error(err);
        }
        this.selection = Array.from(new Set([...this.selection, ...recipeIds]));
      },

      /// Update selectionArchiveState from recipeIds based on the data we have;
      /// used for example by bulk-actions, to enable/disable archive/unarchive buttons.
      /// External usages should use the `.selectionArchiveState` getter (no underscore prefix).
      _updateSnapshotOfSelectionArchiveState(recipeIds) {
        let stateForNew = null;
        recipeIds.forEach((id) => {
          // Only handle newly selected IDs
          if (!this.selection.includes(id)) {
            if (this.archived === false) {
              // Filter currently excludes archived recipes, so this one must be unarchived
              stateForNew = "unarchived";
            } else if (this.archived === true) {
              // Filter currently includes only archived recipes, so this one must be archived
              stateForNew = "archived";
            } else {
              // Indeterminate: we'll know its archived state if it is in visibleRecipes,
              // otherwise record its state as unknown. (While selectionArchiveState has any unknown
              // we'll enable both the Archive and Unarchive buttons.)
              const r = this.visibleRecipes.find((r) => r.id === id);
              if (r) {
                stateForNew = r.archived ? "archived" : "unarchived";
              } else {
                stateForNew = "unknown";
              }
            }

            this._snapshotOfSelectionArchiveState[id] = stateForNew;
          }
        });
      },

      removeFromSelection(recipeIds) {
        const tmp = new Set(this.selection);
        recipeIds.forEach((id) => {
          tmp.delete(id);
          delete this._snapshotOfSelectionArchiveState[id];
        });
        this.selection = Array.from(tmp);
      },

      clearSelection() {
        this.selection = [];
        this._snapshotOfSelectionArchiveState = {};
      },

      setRecipeTypeFilter(key, value) {
        if (this[key] !== value) {
          this[key] = value;
          this.loadRecipePage(1);
        }
      },

      setSort(sortBy, sortDirection) {
        this.sortBy = sortBy;
        if (sortDirection !== undefined) {
          this.sortDirection = sortDirection;
        }

        this.loadRecipePage(1);
      },

      toggleSort(field) {
        if (this.sortBy === field) {
          this.setSort(field, this.sortDirection === "desc" ? "asc" : "desc");
        } else {
          this.setSort(field, "asc");
        }
      },

      // Sets the search from URLSearchParams. The opposite of requestQueryParams getter
      // Removes 'invalid' params
      async setFromSearchParams(searchParams) {
        const newState = {};

        const tagStore = useRecipeTagStore();

        const defaultState = (key) => this.initialSettings[key] || defaults[key];

        const parseTristate = (str) => {
          switch (str) {
            case "true":
            case "1":
              return true;
            case "false":
            case "0":
              return false;
            default:
              return null;
          }
        };

        if (searchParams.has(queryParamMap["q"])) {
          newState["q"] = searchParams.get(queryParamMap["q"]);
        }

        if (searchParams.has(queryParamMap["archived"])) {
          newState["archived"] = parseTristate(searchParams.get(queryParamMap["archived"]));
        }

        if (searchParams.has(queryParamMap["subrecipe"])) {
          newState["subrecipe"] = parseTristate(searchParams.get(queryParamMap["subrecipe"]));
        }

        if (searchParams.has(queryParamMap["empty"])) {
          newState["empty"] = parseTristate(searchParams.get(queryParamMap["empty"]));
        }

        if (searchParams.has(queryParamMap["activeTags"])) {
          const tagNames = searchParams.getAll(queryParamMap["activeTags"]);

          if (!tagStore.loaded) {
            tagStore.setUserId(this.userId);
            await tagStore.loadAllTags();
          }

          newState["activeTags"] = tagNames
            .map((name) => tagStore.getByName(name))
            .filter((tag) => tag);
        }

        if (searchParams.has(queryParamMap["perPage"])) {
          newState["perPage"] =
            parseInt(searchParams.get(queryParamMap["perPage"])) || defaultState("perPage");
        }

        if (searchParams.has(queryParamMap["currentPage"])) {
          newState["currentPage"] =
            parseInt(searchParams.get(queryParamMap["currentPage"])) || defaultState("currentPage");
        }

        if (searchParams.has(queryParamMap["sortBy"])) {
          const sort = validSorts[searchParams.get(queryParamMap["sortBy"])]
            ? searchParams.get(queryParamMap["sortBy"])
            : defaultState("sortBy");
          newState["sortBy"] = sort;
        }

        if (searchParams.has(queryParamMap["sortDirection"])) {
          newState["sortDirection"] =
            searchParams.get(queryParamMap["sortDirection"]) == "asc" ? "asc" : "desc";
        }

        if (searchParams.has(queryParamMap["beginDate"])) {
          const date = validDate(searchParams.get(queryParamMap["beginDate"]))
            ? searchParams.get(queryParamMap["beginDate"])
            : defaultState("beginDate");
          newState["beginDate"] = date;
        }

        if (searchParams.has(queryParamMap["endDate"])) {
          const date = validDate(searchParams.get(queryParamMap["endDate"]))
            ? searchParams.get(queryParamMap["endDate"])
            : defaultState("endDate");
          newState["endDate"] = date;
        }

        this.resetSearch(newState);
      },

      resetSearch(overrides = {}) {
        const { userId, searchEndpoint, initialSettings } = { ...this };
        this.$patch({
          ...defaults,
          ...initialSettings,
          userId: userId,
          searchEndpoint: searchEndpoint,
          initialSettings: initialSettings,
          ...overrides,
        });

        this.loadRecipePage(this.currentPage);
      },
    },
  });

  const store = useStore();
  storeInstances.set(storeName, store);
  return store;
};

export const numberFormat = (num) => new Intl.NumberFormat().format(num);
